/**
 * Operator drive Storage uploads — London Smoke & Cure.
 * Library files: up to 1.4 GB. (Chat attachments stay on operatorChatFileService at 120 MB.)
 * Path: operatorDrive/{scope}/{ownerKey}/{itemId}/{safeFileName}
 */

import { deleteObject, getBlob, getDownloadURL, ref, updateMetadata, uploadBytesResumable } from 'firebase/storage';
import { storage } from './firebase';
import {
    contentDispositionForDriveFile,
    ensureDriveFileExtension,
} from '../utils/operatorDriveFileName';

export const OPERATOR_DRIVE_MAX_FILE_BYTES = Math.floor(1.4 * 1024 * 1024 * 1024);
export const OPERATOR_DRIVE_PREFIX = 'operatorDrive';

export const DRIVE_SCOPE = {
    PERSONAL: 'personal',
    SHARED: 'shared',
};

/**
 * @param {number} bytes
 * @returns {string}
 */
export function formatOperatorDriveBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * @param {File | Blob | null | undefined} file
 * @returns {string | null}
 */
export function validateOperatorDriveFile(file) {
    if (!file) return 'No file selected.';
    const size = Number(file.size);
    if (!Number.isFinite(size) || size < 0) return 'Invalid file.';
    if (size === 0) return 'File is empty.';
    if (size > OPERATOR_DRIVE_MAX_FILE_BYTES) {
        return `File is too large (max ${formatOperatorDriveBytes(OPERATOR_DRIVE_MAX_FILE_BYTES)}).`;
    }
    return null;
}

/**
 * @param {string} name
 * @param {string} [contentType]
 * @returns {string}
 */
export function sanitizeOperatorDriveFileName(name, contentType) {
    const base = String(name || 'file')
        .replace(/[/\\]/g, '_')
        .replace(/[^\w.\- ()[\]]+/g, '_')
        .trim();
    return ensureDriveFileExtension(base || 'file', contentType);
}

/**
 * @param {{ scope: string, ownerUid?: string, itemId: string, fileName: string }} args
 */
export function operatorDriveStoragePath({ scope, ownerUid, itemId, fileName }) {
    const safeScope = scope === DRIVE_SCOPE.SHARED ? DRIVE_SCOPE.SHARED : DRIVE_SCOPE.PERSONAL;
    const ownerKey = safeScope === DRIVE_SCOPE.SHARED ? 'team' : String(ownerUid || '').trim();
    const safeItem = String(itemId || '').trim();
    const safeName = sanitizeOperatorDriveFileName(fileName);
    if (!ownerKey || !safeItem) {
        throw new Error('owner and itemId are required for drive upload.');
    }
    return `${OPERATOR_DRIVE_PREFIX}/${safeScope}/${ownerKey}/${safeItem}/${safeName}`;
}

/**
 * @param {{
 *   file: File,
 *   scope: string,
 *   ownerUid?: string,
 *   itemId: string,
 *   onProgress?: (pct: number) => void,
 * }} args
 */
export function uploadOperatorDriveFile({ file, scope, ownerUid, itemId, onProgress }) {
    const err = validateOperatorDriveFile(file);
    if (err) return Promise.reject(new Error(err));

    const fileName = sanitizeOperatorDriveFileName(file.name || 'file', file.type);
    const storagePath = operatorDriveStoragePath({
        scope,
        ownerUid,
        itemId,
        fileName,
    });
    const contentType = file.type || 'application/octet-stream';
    const objectRef = ref(storage, storagePath);
    const task = uploadBytesResumable(objectRef, file, {
        contentType,
        contentDisposition: contentDispositionForDriveFile(fileName),
        customMetadata: {
            brand: 'London Smoke & Cure',
            scope: String(scope),
            itemId: String(itemId),
        },
    });

    return new Promise((resolve, reject) => {
        task.on(
            'state_changed',
            (snap) => {
                if (typeof onProgress === 'function' && snap.totalBytes > 0) {
                    onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
                }
            },
            (uploadErr) => reject(uploadErr),
            async () => {
                try {
                    const downloadUrl = await getDownloadURL(task.snapshot.ref);
                    resolve({
                        storagePath,
                        fileName,
                        contentType,
                        sizeBytes: Number(file.size) || 0,
                        downloadUrl,
                    });
                } catch (urlErr) {
                    reject(urlErr);
                }
            }
        );
    });
}

/**
 * @param {string} storagePath
 */
export async function deleteOperatorDriveStorageObject(storagePath) {
    if (!storagePath) return;
    try {
        await deleteObject(ref(storage, storagePath));
    } catch (err) {
        // Missing object is fine (already removed).
        if (err?.code !== 'storage/object-not-found') throw err;
    }
}

/**
 * Refresh a download URL for an existing path.
 * @param {string} storagePath
 */
export async function getOperatorDriveDownloadUrl(storagePath) {
    if (!storagePath) throw new Error('storagePath is required.');
    return getDownloadURL(ref(storage, storagePath));
}

/**
 * Read file bytes through the Firebase Storage SDK (signed-in Admin/Production).
 * Prefer this for in-pane preview — avoids iframe/CORS limits on the public download URL.
 * @param {string} storagePath
 * @param {string} [contentType]
 * @returns {Promise<Blob>}
 */
export async function getOperatorDriveFileBlob(storagePath, contentType) {
    if (!storagePath) throw new Error('storagePath is required.');
    const blob = await getBlob(ref(storage, storagePath));
    if (!blob?.size) throw new Error('That Smoke Drive file is empty.');
    const type = String(contentType || blob.type || '').split(';')[0].trim();
    if (type && type !== 'application/octet-stream' && blob.type !== type) {
        return new Blob([blob], { type });
    }
    return blob;
}

/**
 * Hint the browser to save with the stored type (e.g. .jpg), not a generic File.
 * @param {string} storagePath
 * @param {string} fileName
 * @param {string} [contentType]
 */
export async function setOperatorDriveAttachmentDisposition(storagePath, fileName, contentType) {
    if (!storagePath) return;
    const safe = sanitizeOperatorDriveFileName(fileName, contentType);
    /** @type {{ contentDisposition: string, contentType?: string }} */
    const meta = {
        contentDisposition: contentDispositionForDriveFile(safe),
    };
    const type = String(contentType || '').split(';')[0].trim();
    if (type && type !== 'application/octet-stream') {
        meta.contentType = type;
    }
    try {
        await updateMetadata(ref(storage, storagePath), meta);
    } catch {
        /* best effort; download still opens the Storage URL */
    }
}
